import { getActiveLocale } from '@shared/utils/i18n';

/**
 * Tipos y formateadores COMPARTIDOS por todas las pantallas del Portal de Familias (M08).
 *
 * <p>El portal no reutiliza los helpers del back-office a propósito: allí el usuario es personal
 * con formación, aquí es una familia que entra dos veces al año. Todo lo que se pinta se formatea
 * en un solo sitio para que «S/ 1,268.40» y «30/08/2026» se lean igual en las nueve pantallas.</p>
 */

/** Espacio de traducción común del portal (cada pantalla añade el suyo: `etnPortalCore`, …). */
export const PORTAL_I18N = 'etnPortal';

/** `ApiResponse` del backend con `data` tipado (los POST del portal responden así). */
export interface PortalApiResponse<T = undefined> {
  message: string;
  success: boolean;
  action?: string;
  code?: string | null;
  operation?: string;
  data?: T;
}

/** Cuerpo de error del portal (`{ message, status, code?, path }` o el `ApiResponse` de 4xx). */
export interface PortalErrorBody {
  message?: string;
  status?: number;
  code?: string | null;
  path?: string;
  success?: boolean;
}

/** Código que devuelve el gate de consentimiento (409) mientras la política vigente no se acepta. */
export const PORTAL_CONSENT_REQUIRED = 'PORTAL_CONSENT_REQUIRED';

/** Relación de la cuenta con el espacio (nunca se deduce: viaja explícita en el vínculo). */
export type PortalRelationship = 'HOLDER' | 'CO_HOLDER' | 'BENEFICIARY' | 'LEGAL_REPRESENTATIVE';

const RELACIONES: readonly string[] = ['HOLDER', 'CO_HOLDER', 'BENEFICIARY', 'LEGAL_REPRESENTATIVE'];
const TIPOS_DOCUMENTO: readonly string[] = ['DNI', 'CE', 'PASSPORT', 'RUC'];

/** Cierre por inactividad si la marca aún no cargó (el mismo valor por defecto que el backend). */
export const PORTAL_SESSION_MINUTES_DEFAULT = 20;

/** «Representante legal» en vez de `LEGAL_REPRESENTATIVE`; un código nuevo se muestra tal cual. */
export function etiquetaRelacion(t: (clave: string) => string, valor: string | null | undefined): string {
  return valor && RELACIONES.includes(valor) ? t(`etnPortalCore.cuenta.tipo.${valor}`) : (valor ?? '');
}

/** «Pasaporte» en vez de `PASSPORT`; un código nuevo se muestra tal cual. */
export function etiquetaDocumento(t: (clave: string) => string, valor: string | null | undefined): string {
  return valor && TIPOS_DOCUMENTO.includes(valor) ? t(`etnPortalCore.comun.doc.${valor}`) : (valor ?? '');
}

/** Marca del cementerio — `GET /public/company/{ruc}`, lo único público del portal. */
export interface PortalCompanyBranding {
  companyId: number;
  companyName: string;
  tradeName: string | null;
  ruc: string;
  logoUrl: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  scheduleText: string | null;
  consentPolicyVersion: string | null;
  consentPolicyTitle: string | null;
  /** Minutos de inactividad antes del cierre (`portal.session_minutes`). */
  sessionMinutes: number;
}

/** Política de consentimiento vigente (Ley 29733). */
export interface PortalConsentPolicy {
  policyId: number;
  version: string;
  title: string;
  body: string;
  effectiveFrom: string | null;
  isCurrent: boolean | null;
}

/** Estado del consentimiento de la cuenta frente a la política vigente. */
export interface PortalConsentStatus {
  required: boolean;
  policyId: number | null;
  policyVersion: string | null;
  policyTitle: string | null;
  acceptedAt: string | null;
}

/** Segundos que le quedan a la sesión (`GET /auth/session`). */
export interface PortalSessionStatus {
  active: boolean;
  remainingSeconds: number;
  absoluteRemainingSeconds: number;
  consentRequired: boolean;
}

const MONEY_FORMATS = new Map<string, Intl.NumberFormat>();

/** Dos decimales con los separadores del idioma activo (T-11: antes 'es-PE' fijo). */
function moneyFormat(): Intl.NumberFormat {
  const locale = getActiveLocale();
  let format = MONEY_FORMATS.get(locale);
  if (!format) {
    format = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    MONEY_FORMATS.set(locale, format);
  }
  return format;
}

/** `S/ 1,268.40` — siempre con dos decimales y con el símbolo delante, como el comprobante. */
export function money(n: number | string | null | undefined): string {
  const value = typeof n === 'string' ? Number(n) : n;
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'S/ 0.00';
  }
  return `S/ ${moneyFormat().format(value)}`;
}

/** Número sin símbolo (`1,268.40`) para las columnas donde la moneda ya está en la cabecera. */
export function numero(n: number | string | null | undefined, decimals = 2): string {
  const value = typeof n === 'string' ? Number(n) : n;
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return value.toLocaleString(getActiveLocale(), { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/**
 * Convierte a `Date` tanto un `LocalDate` (`2026-08-30`) como un `Instant`
 * (`2026-08-30T14:00:00Z`). El `LocalDate` se ancla al mediodía LOCAL: si se dejara a medianoche
 * UTC, Lima (UTC-5) lo pintaría un día antes y la cuota parecería vencida un día antes.
 */
function parseFecha(iso: string | null | undefined): Date | null {
  if (!iso) {
    return null;
  }
  const soloFecha = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const d = soloFecha ? new Date(`${iso}T12:00:00`) : new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `30/08/2026`. */
export function fechaCorta(iso: string | null | undefined): string {
  const d = parseFecha(iso);
  if (!d) {
    return '—';
  }
  const dd = `${d.getDate()}`.padStart(2, '0');
  const mm = `${d.getMonth() + 1}`.padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

/** `30/08/2026 16:04`. */
export function fechaHora(iso: string | null | undefined): string {
  const d = parseFecha(iso);
  if (!d) {
    return '—';
  }
  const hh = `${d.getHours()}`.padStart(2, '0');
  const mi = `${d.getMinutes()}`.padStart(2, '0');
  return `${fechaCorta(iso)} ${hh}:${mi}`;
}

/** Día del mes en dos cifras (`30`) — para las píldoras de «Próximas fechas». */
export function diaDe(iso: string | null | undefined): string {
  const d = parseFecha(iso);
  return d ? `${d.getDate()}`.padStart(2, '0') : '—';
}

const MESES_CORTOS = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SET', 'OCT', 'NOV', 'DIC'];

/** Mes abreviado en mayúsculas (`AGO`) — para las píldoras de «Próximas fechas». */
export function mesDe(iso: string | null | undefined): string {
  const d = parseFecha(iso);
  return d ? MESES_CORTOS[d.getMonth()] : '';
}

/** `mm:ss` a partir de segundos — cuenta atrás de sesión y de vigencia del código. */
export function mmss(totalSeconds: number | null | undefined): string {
  const s = Math.max(0, Math.floor(totalSeconds ?? 0));
  const mm = `${Math.floor(s / 60)}`.padStart(2, '0');
  const ss = `${s % 60}`.padStart(2, '0');
  return `${mm}:${ss}`;
}

/** Iniciales para el avatar de la cabecera (`Carmen Vargas Ríos` → `CV`). */
export function iniciales(nombre: string | null | undefined): string {
  const partes = (nombre ?? '').trim().split(/\s+/).filter(Boolean);
  if (!partes.length) {
    return '—';
  }
  return (partes[0][0] + (partes[1]?.[0] ?? '')).toUpperCase();
}
